import { useTranslation } from 'react-i18next';
import type { NavBadgeKind } from '@/hooks/useReleaseSections';
import { cn } from '@/utils/cn';

interface NavCountBadgeProps {
  count: number;
  kind: NavBadgeKind;
  /** Show a plain dot instead of the number (used where several items are folded into one) */
  dot?: boolean;
  /** Position/size overrides from the parent (the badge itself is absolutely positioned) */
  className?: string;
}

/** Spoken text of a nav count, e.g. "3 waiting for you". Also used in the nav link's aria-label. */
export const useNavBadgeAria = (): ((count: number, kind: NavBadgeKind) => string) => {
  const { t } = useTranslation('common');
  return (count, kind) =>
    kind === 'awaiting'
      ? t('nav_badge_awaiting_aria', { count })
      : t('nav_badge_newly_released_aria', { count });
};

/**
 * Count badge on a nav item ("99+" above 99). Renders nothing for 0. The number is decorative;
 * the spoken text lives in the sr-only span (and in the link's aria-label where it has one).
 */
export const NavCountBadge = ({ count, kind, dot = false, className }: NavCountBadgeProps) => {
  const aria = useNavBadgeAria();
  if (count <= 0) return null;

  const text = aria(count, kind);
  if (dot) {
    return (
      <span
        className={cn('absolute h-2.5 w-2.5 rounded-full bg-coral-600 ring-2 ring-white', className)}
      >
        <span className="sr-only">{text}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        'absolute flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-coral-600 px-1',
        'text-[10px] font-bold leading-none tabular-nums text-coral-600-contrast ring-2 ring-white',
        className
      )}
    >
      <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
      <span className="sr-only">{text}</span>
    </span>
  );
};
